import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  X, UploadSimple, FileCsv, FileImage, Trash, CircleNotch, Sparkle, CaretDown,
  CheckCircle, ArrowRight, Warning, ArrowsLeftRight, PencilSimple, Prohibit, ArrowsMerge,
} from "@phosphor-icons/react";

const CONF = {
  high: { label: "High", color: "#4A6B53", bg: "#E8F0EA" },
  medium: { label: "Medium", color: "#8A6A3B", bg: "#F6EEDF" },
  low: { label: "Low", color: "#B23B1F", bg: "#FDECE8" },
};
const ACCOUNT_TYPES = ["Asset", "Liability", "Equity", "Income", "Expense"];

function ConfDot({ confidence, reason }) {
  const c = CONF[confidence] || CONF.medium;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: c.bg, color: c.color }} title={reason} aria-label={`${c.label} confidence`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.color }} /> {c.label}
    </span>
  );
}

function ActionBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold border transition-colors"
      style={active ? { background: "var(--bk-accent)", borderColor: "var(--bk-accent)", color: "#fff" } : { borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
      <Icon size={13} weight="bold" /> {label}
    </button>
  );
}

function MappingCard({ m, cats, onAction }) {
  const [picker, setPicker] = useState("");
  const [pickVal, setPickVal] = useState("");
  const isTxn = m.record_type === "transaction";
  const isCat = m.record_type === "category";
  const isAcct = m.record_type === "account";
  const txnCats = isTxn ? (m.mapped_to === "income" ? cats.income : cats.expense) : [];
  const catList = isCat ? (m.category_type === "income" ? cats.income : cats.expense) : [];

  const act = (action, resolved_value, resolved_type) => {
    setPicker("");
    onAction(m.id, action, resolved_value, resolved_type);
  };

  const mappedDisplay = isTxn
    ? `${m.owner_action === "marked_as_transfer" ? "transfer" : m.mapped_to} · ${m.owner_action === "reassigned" && m.owner_resolved_value ? m.owner_resolved_value : m.mapped_category || ""}`
    : (m.owner_action === "reassigned" && m.owner_resolved_value) || m.mapped_to;

  return (
    <div className="rounded-xl border p-3.5" style={{ borderColor: m.is_flagged && m.owner_action === "none" ? "rgba(232,168,56,0.5)" : "var(--bk-border)", background: "#fff" }} data-testid={`qb-mapping-${m.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium" style={{ color: "var(--bk-text)" }}>{m.qb_value}</span>
        {isTxn && m.txn && <span className="text-xs font-mono" style={{ color: m.txn.amount >= 0 ? "var(--bk-success)" : "#B23B1F" }}>{m.txn.amount >= 0 ? "+" : "−"}${Math.abs(m.txn.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>}
        {isTxn && m.txn && <span className="text-xs" style={{ color: "var(--bk-disabled)" }}>{m.txn.date}</span>}
        <span style={{ color: "var(--bk-disabled)" }}>→</span>
        <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{mappedDisplay}</span>
        {m.is_new_category && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "#DCEAFB", color: "#2456A6" }}>New</span>}
        {m.flag_type === "duplicate" && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "#FFE8D9", color: "#B45309" }}>Duplicate</span>}
        <ConfDot confidence={m.confidence} reason={m.flag_reason} />
        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>{m.source_kind === "document" ? "Scanned" : "CSV"}</span>
      </div>
      <p className="text-xs mt-1.5" style={{ color: "var(--bk-text-2)" }}>{m.flag_reason}</p>
      <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
        <ActionBtn active={m.owner_action === "confirmed"} onClick={() => act("confirmed")} icon={CheckCircle} label="Confirm" testid={`qb-confirm-${m.id}`} />
        {(isTxn || isCat) && <ActionBtn active={m.owner_action === "reassigned"} onClick={() => { setPicker(picker === "reassign" ? "" : "reassign"); setPickVal(""); }} icon={ArrowRight} label={isTxn ? "Reassign category" : "Reassign"} testid={`qb-reassign-${m.id}`} />}
        {isAcct && <ActionBtn active={m.owner_action === "reassigned"} onClick={() => { setPicker(picker === "type" ? "" : "type"); setPickVal(""); }} icon={ArrowRight} label="Change type" testid={`qb-changetype-${m.id}`} />}
        {isAcct && m.similar_to && <ActionBtn active={m.owner_action === "merged"} onClick={() => act("merged")} icon={ArrowsMerge} label={`Merge with '${m.similar_to}'`} testid={`qb-merge-${m.id}`} />}
        {isTxn && <ActionBtn active={m.owner_action === "marked_as_transfer"} onClick={() => act("marked_as_transfer")} icon={ArrowsLeftRight} label="Mark as transfer" testid={`qb-transfer-${m.id}`} />}
        {isCat && m.is_new_category && <ActionBtn active={m.owner_action === "renamed"} onClick={() => { setPicker(picker === "rename" ? "" : "rename"); setPickVal(m.new_category_name || ""); }} icon={PencilSimple} label="Rename" testid={`qb-rename-${m.id}`} />}
        <ActionBtn active={m.owner_action === "skipped"} onClick={() => act("skipped")} icon={Prohibit} label="Skip" testid={`qb-skip-${m.id}`} />
      </div>
      {picker === "reassign" && (
        <div className="flex items-center gap-2 mt-2">
          <select value={pickVal} onChange={(e) => setPickVal(e.target.value)} className="px-3 py-1.5 rounded-md border text-sm bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid={`qb-reassign-select-${m.id}`}>
            <option value="">Pick a category…</option>
            {(isTxn ? txnCats : catList).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button disabled={!pickVal} onClick={() => act("reassigned", pickVal)} className="bk-btn-primary px-3 py-1.5 rounded-md text-xs font-semibold disabled:opacity-50" data-testid={`qb-reassign-apply-${m.id}`}>Apply</button>
        </div>
      )}
      {picker === "type" && (
        <div className="flex items-center gap-2 mt-2">
          <select value={pickVal} onChange={(e) => setPickVal(e.target.value)} className="px-3 py-1.5 rounded-md border text-sm bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid={`qb-type-select-${m.id}`}>
            <option value="">Pick account type…</option>
            {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <button disabled={!pickVal} onClick={() => act("reassigned", null, pickVal)} className="bk-btn-primary px-3 py-1.5 rounded-md text-xs font-semibold disabled:opacity-50">Apply</button>
        </div>
      )}
      {picker === "rename" && (
        <div className="flex items-center gap-2 mt-2">
          <input value={pickVal} onChange={(e) => setPickVal(e.target.value)} className="px-3 py-1.5 rounded-md border text-sm flex-1 max-w-xs" style={{ borderColor: "var(--bk-border)" }} data-testid={`qb-rename-input-${m.id}`} />
          <button disabled={!pickVal.trim()} onClick={() => act("renamed", pickVal.trim())} className="bk-btn-primary px-3 py-1.5 rounded-md text-xs font-semibold disabled:opacity-50" data-testid={`qb-rename-apply-${m.id}`}>Apply</button>
        </div>
      )}
      {m.is_flagged && m.owner_action === "none" && (
        <p className="text-[11px] mt-2 font-medium" style={{ color: "#8A6A3B" }} data-testid={`qb-unreviewed-${m.id}`}>
          {m.flag_type === "duplicate" ? "No action taken — will be skipped to avoid doubling up" : "No action taken — will import as mapped"}
        </p>
      )}
    </div>
  );
}

export function QuickBooksImport({ businessId, open, onClose, onImported, resumeSession }) {
  const [step, setStep] = useState("select");
  const [files, setFiles] = useState([]);
  const [session, setSession] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [cats, setCats] = useState({ income: [], expense: [] });
  const [tab, setTab] = useState("transaction");
  const [showAuto, setShowAuto] = useState(false);
  const [commitOpen, setCommitOpen] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const csvRef = useRef(null);
  const docRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get(`/businesses/${businessId}/categories`).then((r) => setCats(r.data)).catch(() => {});
    if (resumeSession) {
      setSession(resumeSession);
      if (resumeSession.status === "processing") { setStep("processing"); startPoll(resumeSession.id); }
      else loadReview(resumeSession.id, true);
    } else {
      setStep("select"); setFiles([]); setSession(null); setMappings([]); setResult(null);
    }
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addFiles = (list, kind) => {
    const next = [...files];
    Array.from(list || []).forEach((f) => {
      if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(Object.assign(f, { _kind: kind }));
    });
    setFiles(next);
  };

  const startPoll = (sid) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/businesses/${businessId}/migration/quickbooks/sessions/${sid}`);
        setSession(data);
        if (data.status === "review") { clearInterval(pollRef.current); setStep("summary"); }
        else if (data.status === "failed") { clearInterval(pollRef.current); setStep("failed"); }
      } catch { /* keep polling */ }
    }, 2500);
  };

  const upload = async () => {
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/businesses/${businessId}/migration/quickbooks/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSession({ id: data.session_id, status: "processing" });
      setStep("processing");
      startPoll(data.session_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start the import.");
    } finally { setUploading(false); }
  };

  const loadReview = async (sid, jumpToReview = false) => {
    try {
      const [s, mp] = await Promise.all([
        api.get(`/businesses/${businessId}/migration/quickbooks/sessions/${sid}`),
        api.get(`/businesses/${businessId}/migration/quickbooks/sessions/${sid}/mappings`),
      ]);
      setSession(s.data);
      setMappings(mp.data.mappings || []);
      setStep(jumpToReview ? "review" : "summary");
    } catch { toast.error("Couldn't load the review."); }
  };

  const onAction = async (mappingId, action, resolved_value, resolved_type) => {
    const prev = mappings;
    setMappings((ms) => ms.map((m) => (m.id === mappingId ? { ...m, owner_action: action, owner_resolved_value: resolved_value || m.owner_resolved_value, ...(action === "renamed" && resolved_value ? { new_category_name: resolved_value, mapped_to: resolved_value } : {}), ...(action === "reassigned" && resolved_value ? { mapped_to: resolved_value } : {}), ...(action === "reassigned" && resolved_type ? { mapped_to: resolved_type } : {}) } : m)));
    try {
      await api.post(`/businesses/${businessId}/migration/quickbooks/sessions/${session.id}/mappings/${mappingId}/action`, { action, resolved_value, resolved_type });
    } catch (err) {
      setMappings(prev);
      toast.error(err.response?.data?.detail || "Couldn't save that action.");
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post(`/businesses/${businessId}/migration/quickbooks/sessions/${session.id}/commit`);
      setResult(data);
      setCommitOpen(false);
      setStep("success");
      onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed — nothing was saved. Try again.");
      setCommitOpen(false);
    } finally { setCommitting(false); }
  };

  const startOver = async () => {
    if (session?.id) { try { await api.post(`/businesses/${businessId}/migration/quickbooks/sessions/${session.id}/abandon`); } catch { /* ignore */ } }
    setStep("select"); setFiles([]); setSession(null); setMappings([]);
  };

  if (!open) return null;

  const tabMaps = mappings.filter((m) => m.record_type === tab);
  const flaggedInTab = tabMaps.filter((m) => m.is_flagged);
  const autoInTab = tabMaps.filter((m) => !m.is_flagged);
  const unreviewedFlagged = mappings.filter((m) => m.is_flagged && m.owner_action === "none" && m.flag_type !== "duplicate").length;
  const counts = {
    transaction: mappings.filter((m) => m.record_type === "transaction"),
    account: mappings.filter((m) => m.record_type === "account"),
    category: mappings.filter((m) => m.record_type === "category"),
  };
  const skippedCount = mappings.filter((m) => m.owner_action === "skipped" || (m.flag_type === "duplicate" && m.owner_action === "none")).length;
  const newCats = counts.category.filter((m) => m.is_new_category && m.owner_action !== "reassigned" && m.owner_action !== "skipped").length;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: "var(--bk-page)" }} data-testid="qb-import-screen">
      <header className="sticky top-0 z-10 border-b px-6 h-16 flex items-center justify-between" style={{ borderColor: "var(--bk-border)", background: "var(--bk-surface)" }}>
        <div className="flex items-center gap-2.5">
          <Sparkle size={20} weight="duotone" color="var(--bk-accent)" />
          <span className="font-display text-lg font-bold" style={{ color: "var(--bk-text)" }}>Import from QuickBooks</span>
        </div>
        <div className="flex items-center gap-3">
          {step === "review" && <button onClick={onClose} className="text-sm font-semibold hover:underline" style={{ color: "var(--bk-text-2)" }} data-testid="qb-save-later">Save and continue later</button>}
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F5F5F0]" data-testid="qb-close" aria-label="Close"><X size={20} weight="bold" color="var(--bk-text-2)" /></button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 pb-32">
        {step === "select" && (
          <div>
            <p className="text-base mb-6" style={{ color: "var(--bk-text-2)" }}>Upload your QuickBooks files and Rowan will handle the mapping.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bk-card p-6 text-center cursor-pointer hover:border-[var(--bk-accent)] transition-colors" onClick={() => csvRef.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && csvRef.current?.click()} data-testid="qb-csv-zone">
                <FileCsv size={34} weight="duotone" color="var(--bk-accent)" className="mx-auto" />
                <p className="font-semibold mt-2" style={{ color: "var(--bk-text)" }}>QuickBooks CSV export</p>
                <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>Export from QuickBooks: Reports → All Transactions → Export to Excel or CSV</p>
                <input ref={csvRef} type="file" accept=".csv" multiple className="hidden" onChange={(e) => { addFiles(e.target.files, "csv"); e.target.value = ""; }} data-testid="qb-csv-input" />
              </div>
              <div className="bk-card p-6 text-center cursor-pointer hover:border-[var(--bk-accent)] transition-colors" onClick={() => docRef.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && docRef.current?.click()} data-testid="qb-doc-zone">
                <FileImage size={34} weight="duotone" color="var(--bk-accent)" className="mx-auto" />
                <p className="font-semibold mt-2" style={{ color: "var(--bk-text)" }}>Scanned reports or photos</p>
                <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>Upload a photo or scan of any QuickBooks report — Rowan will read what she can</p>
                <input ref={docRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple className="hidden" onChange={(e) => { addFiles(e.target.files, "doc"); e.target.value = ""; }} data-testid="qb-doc-input" />
              </div>
            </div>
            {files.length > 0 && (
              <div className="mt-5 space-y-2" data-testid="qb-file-list">
                {files.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="flex items-center gap-3 rounded-lg border px-3 py-2 bg-white text-sm" style={{ borderColor: "var(--bk-border)" }}>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>{f._kind === "csv" ? "CSV" : "Document"}</span>
                    <span className="flex-1 truncate" style={{ color: "var(--bk-text)" }}>{f.name}</span>
                    <span className="text-xs" style={{ color: "var(--bk-disabled)" }}>{(f.size / 1024).toFixed(0)} KB</span>
                    <button onClick={() => setFiles(files.filter((_, j) => j !== i))} data-testid={`qb-remove-file-${i}`} aria-label="Remove file"><Trash size={15} color="var(--bk-text-2)" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-4 mt-6">
              <button onClick={upload} disabled={files.length === 0 || uploading} className="bk-btn-primary px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="qb-next-btn">
                {uploading ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} weight="bold" />} Next: Let Rowan review these
              </button>
              <button onClick={onClose} className="text-sm hover:underline" style={{ color: "var(--bk-text-2)" }}>Cancel</button>
            </div>
          </div>
        )}

        {step === "processing" && (
          <div className="text-center py-20" data-testid="qb-processing">
            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center animate-pulse" style={{ background: "var(--bk-rowan-bg)" }}>
              <Sparkle size={30} weight="duotone" color="var(--bk-accent)" />
            </div>
            <h2 className="font-display text-2xl font-bold mt-5" style={{ color: "var(--bk-text)" }}>Rowan is reading your files…</h2>
            <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }}>This usually takes under a minute. She'll flag anything she needs your help with.</p>
            <div className="w-56 h-1.5 rounded-full mx-auto mt-6 overflow-hidden" style={{ background: "var(--bk-border)" }}>
              <div className="h-full rounded-full animate-pulse" style={{ width: "60%", background: "var(--bk-accent)" }} />
            </div>
          </div>
        )}

        {step === "failed" && (
          <div className="text-center py-16" data-testid="qb-failed">
            <Warning size={38} weight="duotone" color="#B23B1F" className="mx-auto" />
            <h2 className="font-display text-2xl font-bold mt-4" style={{ color: "var(--bk-text)" }}>That didn't work</h2>
            <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: "var(--bk-text-2)" }}>{session?.error_message || "None of your files could be processed. Check that they're valid QuickBooks exports and try again."}</p>
            <button onClick={startOver} className="bk-btn-primary px-6 py-2.5 text-sm font-semibold mt-6" data-testid="qb-failed-retry">Try again</button>
          </div>
        )}

        {step === "summary" && session && (
          <div className="max-w-xl mx-auto" data-testid="qb-parse-summary">
            <h2 className="font-display text-2xl font-bold" style={{ color: "var(--bk-text)" }}>Rowan read {session.files?.length || 0} file{(session.files?.length || 0) !== 1 ? "s" : ""}</h2>
            <div className="bk-card p-6 mt-4 space-y-2 text-sm" style={{ color: "var(--bk-text)" }}>
              <p><b>{session.transaction_count_total}</b> transactions found</p>
              <p><b>{session.account_count_total}</b> accounts from your chart of accounts</p>
              <p><b>{session.category_count_total}</b> categories detected</p>
              {session.flagged_count_total > 0 && <p style={{ color: "#8A6A3B" }}><b>{session.flagged_count_total}</b> items need your attention — Rowan flagged these for review before importing</p>}
              {session.unreadable_count > 0 && <p style={{ color: "#B23B1F" }}><b>{session.unreadable_count}</b> items couldn't be read — from scanned documents</p>}
              {session.category_count_new > 0 && <p style={{ color: "#2456A6" }}>Rowan created <b>{session.category_count_new}</b> new categories that don't exist in IronRoot yet</p>}
            </div>
            {session.unreadable_count > 0 && (
              <div className="rounded-xl p-3.5 mt-3 text-xs" style={{ background: "#FDECE8", color: "var(--bk-text)" }}>Some rows in your scanned documents were too unclear to read. Rowan skipped them.</div>
            )}
            {session.multi_currency_detected && (
              <div className="rounded-xl p-3.5 mt-3 text-xs" style={{ background: "#F6EEDF", color: "var(--bk-text)" }} data-testid="qb-multicurrency-warning">Your QuickBooks export includes transactions in multiple currencies. IronRoot currently works in a single currency — foreign-currency transactions will be flagged for your review.</div>
            )}
            {session.prior_import_at && (
              <div className="rounded-xl p-3.5 mt-3 text-xs" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-text)" }}>You've imported from QuickBooks before (last import: {session.prior_import_at.slice(0, 10)}). Rowan has automatically flagged any transactions that may already be in your books.</div>
            )}
            <div className="flex items-center gap-4 mt-6">
              <button onClick={() => loadReview(session.id, true)} className="bk-btn-primary px-6 py-3 text-sm font-semibold" data-testid="qb-review-btn">Review Rowan's work</button>
              <button onClick={startOver} className="text-sm hover:underline" style={{ color: "var(--bk-text-2)" }} data-testid="qb-start-over">Start over</button>
            </div>
          </div>
        )}

        {step === "review" && session && (
          <div data-testid="qb-review-screen">
            <h2 className="font-display text-2xl font-bold" style={{ color: "var(--bk-text)" }}>Review Rowan's work</h2>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Nothing has been imported yet. Review Rowan's decisions and commit when you're ready.</p>
            <div className="rounded-full border px-4 py-2.5 mt-4 text-sm" style={{ borderColor: "rgba(232,168,56,0.4)", background: "var(--bk-rowan-bg)", color: "var(--bk-text)" }} data-testid="qb-summary-chip">
              Rowan mapped <b>{counts.transaction.length}</b> transactions, <b>{counts.account.length}</b> accounts, and <b>{counts.category.length}</b> categories.{" "}
              {newCats > 0 && <>She created <b>{newCats}</b> new categories{unreviewedFlagged > 0 ? " and" : "."} </>}
              {unreviewedFlagged > 0 ? <>flagged <b>{mappings.filter((m) => m.is_flagged).length}</b> items for your attention.</> : newCats === 0 ? "All mappings look clean." : ""}
            </div>
            <div className="flex gap-2 mt-5 flex-wrap">
              {[["transaction", "Transactions"], ["account", "Accounts"], ["category", "Categories"]].map(([key, label]) => {
                const list = counts[key];
                const fl = list.filter((m) => m.is_flagged).length;
                const isNew = key === "category" ? list.filter((m) => m.is_new_category).length : 0;
                return (
                  <button key={key} onClick={() => setTab(key)} className="px-4 py-2 rounded-full text-sm font-semibold border" data-testid={`qb-tab-${key}`}
                    style={tab === key ? { background: "var(--bk-text)", color: "#fff", borderColor: "var(--bk-text)" } : { borderColor: "var(--bk-border)", color: "var(--bk-text)", background: "#fff" }}>
                    {label} ({list.length}{fl > 0 && key !== "category" ? <span style={{ color: tab === key ? "#F0C36D" : "#8A6A3B" }}> · {fl} flagged</span> : null}{isNew > 0 ? <span style={{ color: tab === key ? "#9CC3F5" : "#2456A6" }}> · {isNew} new</span> : null})
                  </button>
                );
              })}
            </div>
            <div className="mt-5 space-y-2.5">
              {tab === "category" && counts.category.length > 0 && counts.category.every((m) => m.is_new_category) && (
                <div className="rounded-xl p-3.5 text-xs" style={{ background: "#DCEAFB", color: "#2456A6" }}>All of your QuickBooks categories are new to IronRoot. Rowan has named and typed each one — review and rename any that don't look right before importing.</div>
              )}
              {tabMaps.length === 0 && <p className="text-sm py-8 text-center" style={{ color: "var(--bk-text-2)" }}>No {tab === "transaction" ? "transactions" : tab === "account" ? "accounts" : "categories"} were found in your files.</p>}
              {flaggedInTab.length > 0 && flaggedInTab.every((m) => m.owner_action !== "none") && (
                <div className="rounded-xl p-3 text-xs font-semibold" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }} data-testid="qb-all-reviewed-chip">All flagged items reviewed.</div>
              )}
              {flaggedInTab.map((m) => <MappingCard key={m.id} m={m} cats={cats} onAction={onAction} />)}
              {autoInTab.length > 0 && (
                <div className="rounded-xl border" style={{ borderColor: "var(--bk-border)", background: "#fff" }}>
                  <button onClick={() => setShowAuto((s) => !s)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold" style={{ color: "var(--bk-text)" }} data-testid="qb-auto-toggle">
                    Auto-confirmed by Rowan ({autoInTab.length} items)
                    <CaretDown size={16} weight="bold" style={{ transform: showAuto ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                  </button>
                  {showAuto && <div className="px-3 pb-3 space-y-2.5">{autoInTab.map((m) => <MappingCard key={m.id} m={m} cats={cats} onAction={onAction} />)}</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {step === "success" && result && (
          <div className="max-w-xl mx-auto text-center py-10" data-testid="qb-success">
            <CheckCircle size={46} weight="duotone" color="var(--bk-success)" className="mx-auto" />
            <h2 className="font-display text-3xl font-bold mt-4" style={{ color: "var(--bk-text)" }}>Import complete.</h2>
            <div className="bk-card p-6 mt-5 text-left space-y-2 text-sm" style={{ color: "var(--bk-text)" }}>
              <p><b>{result.transactions_committed}</b> transactions added to your ledger{result.transfers_committed > 0 ? ` (+${result.transfers_committed} transfers kept out of P&L)` : ""}</p>
              <p><b>{result.accounts_committed}</b> accounts added to your chart of accounts</p>
              <p><b>{result.categories_committed}</b> categories — including <b>{result.new_categories_created}</b> new ones Rowan created</p>
              <p><b>{result.skipped}</b> items skipped</p>
              {result.duplicates_skipped > 0 && <p style={{ color: "#8A6A3B" }}>Duplicate transactions detected: <b>{result.duplicates_skipped}</b> — skipped automatically</p>}
            </div>
            <p className="text-sm mt-4" style={{ color: "var(--bk-text-2)" }}>{result.rowan_message}</p>
            <button onClick={onClose} className="bk-btn-primary px-6 py-3 text-sm font-semibold mt-6" data-testid="qb-go-to-ledger">Go to ledger</button>
          </div>
        )}
      </main>

      {step === "review" && (
        <footer className="fixed bottom-0 left-0 right-0 border-t px-6 py-4" style={{ borderColor: "var(--bk-border)", background: "var(--bk-surface)" }}>
          <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-3">
            {unreviewedFlagged > 0 ? (
              <p className="text-xs font-semibold inline-flex items-center gap-1.5" style={{ color: "#8A6A3B" }} data-testid="qb-commit-warning">
                <Warning size={15} weight="fill" /> {unreviewedFlagged} flagged item{unreviewedFlagged !== 1 ? "s" : ""} haven't been reviewed — they'll import as Rowan mapped them.
              </p>
            ) : <span />}
            <button onClick={() => setCommitOpen(true)} className="px-6 py-3 rounded-md text-sm font-semibold text-white" data-testid="qb-import-btn"
              style={{ background: "var(--bk-accent)", border: unreviewedFlagged > 0 ? "2px solid #E8A838" : "2px solid transparent" }}>
              {unreviewedFlagged > 0 ? "Import anyway" : "Import to IronRoot"}
            </button>
          </div>
        </footer>
      )}

      {commitOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.4)" }} onKeyDown={(e) => e.key === "Escape" && setCommitOpen(false)}>
          <div className="bk-card p-6 max-w-md w-full bg-white" data-testid="qb-commit-modal" role="dialog">
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Ready to import to IronRoot?</h3>
            <ul className="text-sm mt-3 space-y-1" style={{ color: "var(--bk-text)" }}>
              <li><b>{counts.transaction.length - skippedCount > 0 ? counts.transaction.filter((m) => m.owner_action !== "skipped" && !(m.flag_type === "duplicate" && m.owner_action === "none")).length : 0}</b> transactions</li>
              <li><b>{counts.account.filter((m) => m.owner_action !== "skipped").length}</b> accounts</li>
              <li><b>{counts.category.filter((m) => m.owner_action !== "skipped").length}</b> categories ({newCats} new)</li>
              <li><b>{skippedCount}</b> items skipped</li>
            </ul>
            <p className="text-xs mt-3" style={{ color: "var(--bk-text-2)" }}>This will add everything above to your books. You can edit individual records after importing.</p>
            <div className="flex items-center gap-3 mt-5">
              <button onClick={commit} disabled={committing} className="bk-btn-primary px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="qb-confirm-import">
                {committing ? "Importing…" : "Confirm import"}
              </button>
              <button onClick={() => setCommitOpen(false)} className="text-sm hover:underline" style={{ color: "var(--bk-text-2)" }} data-testid="qb-go-back">Go back</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
